import { httpClient } from '../client';
import {
  Resource,
  ResourceResponse,
  CreateResourceDTO,
  UpdateResourceDTO,
} from '../../../types/resource';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { tokenService } from '../auth/token.service';

class ResourceEndpoints {
  private static instance: ResourceEndpoints;
  private readonly BASE_PATH = `${import.meta.env.VITE_API_BASE_URL}/organisations`;

  private constructor() {}

  static getInstance(): ResourceEndpoints {
    if (!ResourceEndpoints.instance) {
      ResourceEndpoints.instance = new ResourceEndpoints();
    }
    return ResourceEndpoints.instance;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await tokenService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async listResources(organizationId: string): Promise<Resource[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<ResourceResponse>(
        `${this.BASE_PATH}/${organizationId}/resources`,
        { headers }
      );

      logger.info('Resources fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch resources:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createResource(organizationId: string, data: CreateResourceDTO): Promise<Resource> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<{ data: Resource }>(
        `${this.BASE_PATH}/${organizationId}/resources`,
        data,
        { headers }
      );

      logger.info('Resource created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create resource:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async updateResource(
    organizationId: string,
    resourceId: number,
    data: UpdateResourceDTO
  ): Promise<Resource> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<{ data: Resource }>(
        `${this.BASE_PATH}/${organizationId}/resources/${resourceId}`,
        data,
        { headers }
      );

      logger.info('Resource updated successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update resource:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteResource(organizationId: string, resourceId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.BASE_PATH}/${organizationId}/resources/${resourceId}`, {
        headers,
      });

      logger.info('Resource deleted successfully');
    } catch (error) {
      logger.error('Failed to delete resource:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const resourceEndpoints = ResourceEndpoints.getInstance();
